import React from 'react';
import { motion } from 'framer-motion';
import { UsersIcon, MegaphoneIcon, ArrowRight, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useIsMobile } from '../lib/useIsMobile';
export function ServicesSection() {
  const isMobile = useIsMobile();
  const bounceTransition = {
    type: 'spring',
    stiffness: 260,
    damping: 20
  };
  return <section className="relative z-10 py-20 px-4 md:px-8 max-w-7xl mx-auto overflow-hidden" data-id="element-139">
      {/* Decorative blob */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-orange-200 blob opacity-30" data-id="element-140" />

      <div className="text-center mb-16 md:mb-20 relative z-10" data-id="element-141">
        <motion.h2 initial={{
        scale: 0.8,
        opacity: 0
      }} whileInView={{
        scale: 1,
        opacity: 1
      }} viewport={{
        once: true
      }} transition={bounceTransition} className="text-5xl md:text-7xl lg:text-8xl font-fredoka font-bold text-gray-800 mb-4" data-id="element-142">
          Our{' '}
          <motion.span className="inline-block px-6 py-3 bg-gradient-to-r from-orange-400 to-red-400 text-white rounded-3xl border-4 border-gray-800 shadow-[6px_6px_0px_rgba(45,55,72,1)]" animate={{
          rotate: [2, -2, 2],
          y: [0, -8, 0]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-143">
            Services
          </motion.span>
        </motion.h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 relative z-10" data-id="element-144">
        {/* Talent Management Card */}
        <Link to="/talent" className="block group h-full" data-id="element-145">
          <motion.div initial={{
          x: -50,
          opacity: 0
        }} whileInView={{
          x: 0,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={bounceTransition} className="relative h-full p-8 md:p-10 rounded-3xl bg-white border-4 border-gray-800 shadow-[12px_12px_0px_rgba(167,139,250,0.4)] hover:shadow-[16px_16px_0px_rgba(167,139,250,0.5)] hover:translate-x-[-4px] hover:translate-y-[-4px] transition-all duration-300" data-id="element-146">
            {/* Icon */}
            <div className="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-400 to-pink-400 border-3 border-gray-800 text-white group-hover:rotate-12 group-hover:scale-110 transition-all duration-300" data-id="element-147">
              <UsersIcon className="w-8 h-8" data-id="element-148" />
            </div>

            {/* Stars decoration */}
            <div className="absolute top-6 right-6 flex gap-1" data-id="element-149">
              <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-150" />
              <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-151" />
              <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-152" />
            </div>

            <h3 className="text-4xl md:text-5xl font-fredoka font-bold text-gray-800 mb-4 group-hover:text-purple-600 transition-colors" data-id="element-153">
              Talent Management
            </h3>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-6 font-medium" data-id="element-154">
              We discover, nurture, and elevate the voices that shape culture.
              Our 360-degree approach ensures long-term career growth and
              creative freedom.
            </p>

            <div className="flex items-center gap-3 text-purple-600 font-bold group-hover:gap-5 transition-all" data-id="element-155">
              <span className="text-sm tracking-wide uppercase" data-id="element-156">
                Explore Talent Services
              </span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" data-id="element-157" />
            </div>
          </motion.div>
        </Link>

        {/* Influencer Marketing Card */}
        <Link to="/brands" className="block group h-full" data-id="element-158">
          <motion.div initial={{
          x: 50,
          opacity: 0
        }} whileInView={{
          x: 0,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          ...bounceTransition,
          delay: 0.1
        }} className="relative h-full p-8 md:p-10 rounded-3xl bg-white border-4 border-gray-800 shadow-[12px_12px_0px_rgba(255,211,61,0.4)] hover:shadow-[16px_16px_0px_rgba(255,211,61,0.5)] hover:translate-x-[-4px] hover:translate-y-[-4px] transition-all duration-300" data-id="element-159">
            {/* Icon */}
            <div className="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-yellow-400 to-orange-400 border-3 border-gray-800 text-white group-hover:rotate-12 group-hover:scale-110 transition-all duration-300" data-id="element-160">
              <MegaphoneIcon className="w-8 h-8" data-id="element-161" />
            </div>

            {/* Stars decoration */}
            <div className="absolute top-6 right-6 flex gap-1" data-id="element-162">
              <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-163" />
              <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-164" />
              <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-165" />
            </div>

            <h3 className="text-4xl md:text-5xl font-fredoka font-bold text-gray-800 mb-4 group-hover:text-orange-600 transition-colors" data-id="element-166">
              Influencer Marketing
            </h3>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-6 font-medium" data-id="element-167">
              Connecting brands with authentic storytellers. We craft
              data-driven campaigns that amplify your message and drive
              meaningful engagement.
            </p>

            <div className="flex items-center gap-3 text-orange-600 font-bold group-hover:gap-5 transition-all" data-id="element-168">
              <span className="text-sm tracking-wide uppercase" data-id="element-169">
                Explore Brand Services
              </span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" data-id="element-170" />
            </div>
          </motion.div>
        </Link>
      </div>
    </section>;
}